import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Flame, Check, ShoppingCart, Package, Clock, Leaf, Shield, Minus, Plus, Star, X, Truck, Sparkles, ChefHat, Droplets, Wine, UtensilsCrossed, Mail, Phone, MapPin, Instagram, Facebook, Twitter, ChevronDown, ChevronUp, MessageCircle, HelpCircle, Send, ArrowRight, CreditCard, RefreshCw, Award } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Product images mapping
const PRODUCT_IMAGES = {
  emberbento: [
    `${BACKEND_URL}/api/promo-images/bento_grill_kit_main`,
    `${BACKEND_URL}/api/promo-images/emberbento_whats_inside_new`,
    `${BACKEND_URL}/api/promo-images/emberbento_kit`,
    `${BACKEND_URL}/api/promo-images/korean_bento`,
  ],
  grillbox: [
    `${BACKEND_URL}/api/promo-images/magic_box_exploded`,
    `${BACKEND_URL}/api/promo-images/promo_dark`,
    `${BACKEND_URL}/api/promo-images/user_guide`,
    `${BACKEND_URL}/api/promo-images/canada_bbq`,
  ],
  bundle: [
    `${BACKEND_URL}/api/promo-images/bento_grill_kit_main`,
  ]
};

// Star rating component
const StarRating = ({ rating, size = "sm", interactive = false, onChange }) => {
  const starSize = size === "lg" ? "w-6 h-6" : "w-5 h-5";
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map((star) => (
        <button
          key={star}
          onClick={() => interactive && onChange?.(star)}
          className={interactive ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}
          disabled={!interactive}
        >
          <Star className={`${starSize} transition-colors ${star <= rating ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
        </button>
      ))}
    </div>
  );
};

// FAQ Accordion Item
const FAQItem = ({ faq, isOpen, onClick }) => (
  <div className="border border-[#E5E0D8] rounded-xl overflow-hidden">
    <button
      onClick={onClick}
      className="w-full p-5 flex items-center justify-between bg-white hover:bg-[#FAF8F5] transition-colors text-left"
    >
      <span className="font-bold text-[#1A1A1A] pr-4">{faq.question}</span>
      {isOpen ? <ChevronUp className="w-5 h-5 text-[#E25822] shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#8C8C8C] shrink-0" />}
    </button>
    {isOpen && (
      <div className="p-5 pt-0 bg-white">
        <p className="text-[#5C5C5C] leading-relaxed">{faq.answer}</p>
      </div>
    )}
  </div>
);

// Review card
const ReviewCard = ({ review }) => (
  <div className="bg-white rounded-2xl p-6 border border-[#E5E0D8] hover-lift">
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E25822] to-[#B43A1B] flex items-center justify-center text-white font-bold text-lg">
          {review.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="font-bold text-[#1A1A1A]">{review.name}</p>
          {review.verified_purchase && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Verified Purchase</span>
          )}
        </div>
      </div>
      <StarRating rating={review.rating} />
    </div>
    <p className="text-[#5C5C5C] leading-relaxed">{review.comment}</p>
  </div>
);

function App() {
  // State
  const [products, setProducts] = useState([]);
  const [foodOptions, setFoodOptions] = useState(null);
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem("tsbbq_cart");
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showCart, setShowCart] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [reviews, setReviews] = useState({});
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({ name: "", rating: 5, comment: "" });
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState("shop");
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [storeInfo, setStoreInfo] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [contactStatus, setContactStatus] = useState(null);
  
  // Food choices for EmberBentoBox
  const [foodChoices, setFoodChoices] = useState({
    protein: "beef",
    sauce: "teriyaki",
    drink: "none"
  });

  // Show toast notification
  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  // Persist cart across reloads / Stripe redirect
  useEffect(() => {
    try { localStorage.setItem("tsbbq_cart", JSON.stringify(cart)); } catch {}
  }, [cart]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, foodRes, faqRes, storeRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/food-options`),
          axios.get(`${API}/faq`),
          axios.get(`${API}/store-info`)
        ]);
        setProducts(productsRes.data.products);
        setFoodOptions(foodRes.data);
        setFaqs(faqRes.data.faqs);
        setStoreInfo(storeRes.data);
        if (productsRes.data.products.length > 0) {
          setSelectedProduct(productsRes.data.products[0]);
        }
      } catch (e) {
        console.error("Failed to fetch data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch reviews when product changes
  useEffect(() => {
    if (selectedProduct) {
      fetchReviews(selectedProduct.id);
    }
  }, [selectedProduct]);

  const fetchReviews = async (productId) => {
    try {
      const res = await axios.get(`${API}/reviews/${productId}`);
      setReviews(prev => ({ ...prev, [productId]: res.data }));
    } catch (e) {
      console.error("Failed to fetch reviews:", e);
    }
  };

  const calculateItemPrice = (product, choices) => {
    let price = product.price;
    if (product.has_food_options && choices && foodOptions) {
      const protein = foodOptions.proteins.find(p => p.id === choices.protein);
      const drink = foodOptions.drinks.find(d => d.id === choices.drink);
      if (protein) price += protein.price;
      if (drink) price += drink.price;
    }
    return price;
  };

  const addToCart = (product, qty = 1, choices = null) => {
    const itemPrice = calculateItemPrice(product, choices);
    const cartItemId = product.has_food_options ? 
      `${product.id}-${choices?.protein}-${choices?.sauce}-${choices?.drink}` : 
      product.id;
    
    const existing = cart.find(item => item.cartItemId === cartItemId);
    if (existing) {
      setCart(cart.map(item => 
        item.cartItemId === cartItemId ? { ...item, qty: item.qty + qty } : item
      ));
    } else {
      setCart([...cart, { ...product, cartItemId, qty, itemPrice, food_choices: choices }]);
    }
    showToast("Added to cart!");
  };

  const removeFromCart = (cartItemId) => {
    setCart(cart.filter(item => item.cartItemId !== cartItemId));
  };

  const updateCartQty = (cartItemId, newQty) => {
    if (newQty < 1) removeFromCart(cartItemId);
    else setCart(cart.map(item => item.cartItemId === cartItemId ? { ...item, qty: newQty } : item));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.itemPrice * item.qty), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);
  const shippingCost = cartTotal >= 50 ? 0 : 5.99;

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    setCheckoutLoading(true);
    try {
      const items = cart.map(item => ({
        product_id: item.id,
        quantity: item.qty,
        food_choices: item.food_choices
      }));
      const res = await axios.post(`${API}/checkout/create-session`, {
        items,
        origin_url: window.location.origin
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) {
      console.error("Checkout error:", e);
      showToast("Checkout failed. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const submitReview = async () => {
    if (!newReview.name || !newReview.comment) return;
    try {
      await axios.post(`${API}/reviews`, { product_id: selectedProduct.id, ...newReview });
      fetchReviews(selectedProduct.id);
      setNewReview({ name: "", rating: 5, comment: "" });
      setShowReviewForm(false);
      showToast("Review submitted!");
    } catch (e) {
      console.error("Failed to submit review:", e);
    }
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    try {
      const res = await axios.post(`${API}/newsletter?email=${encodeURIComponent(newsletterEmail)}`);
      setNewsletterStatus({ success: true, message: res.data.message });
      setNewsletterEmail("");
    } catch (e) {
      setNewsletterStatus({ success: false, message: "Failed to subscribe. Please try again." });
    }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) return;
    try {
      const params = new URLSearchParams(contactForm).toString();
      const res = await axios.post(`${API}/contact?${params}`);
      setContactStatus({ success: true, message: res.data.message });
      setContactForm({ name: "", email: "", subject: "", message: "" });
    } catch (e) {
      setContactStatus({ success: false, message: "Failed to send message. Please try again." });
    }
  };

  const productReviews = selectedProduct ? reviews[selectedProduct.id] : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <div className="text-center">
          <Flame className="w-16 h-16 text-[#E25822] mx-auto animate-pulse" />
          <p className="mt-4 font-bold text-[#2A2A2A]" style={{ fontFamily: 'Outfit' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#2A2A2A] text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 animate-fade-up">
          <Check className="w-5 h-5 text-green-400" />
          <span className="font-medium">{toast}</span>
        </div>
      )}

      {/* Announcement Bar */}
      <div className="bg-[#E25822] text-white py-2 px-4 text-center text-sm font-medium">
        <span className="flex items-center justify-center gap-2">
          <Truck className="w-4 h-4" />
          FREE SHIPPING on orders over $50 | 30-Day Returns
          <Sparkles className="w-4 h-4" />
        </span>
      </div>

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCart(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col animate-scale-in">
            <div className="p-6 border-b border-[#E5E0D8] flex items-center justify-between">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>Your Cart ({cartCount})</h2>
              <button data-testid="close-cart" onClick={() => setShowCart(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-6">
              {cart.length === 0 ? (
                <div className="text-center py-16">
                  <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-[#5C5C5C] mb-4">Your cart is empty</p>
                  <button onClick={() => { setShowCart(false); setActiveTab("shop"); }} className="text-[#E25822] font-bold hover:underline">
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map(item => (
                    <div key={item.cartItemId} className="flex gap-4 p-4 bg-[#FAF8F5] rounded-xl">
                      <img src={PRODUCT_IMAGES[item.id]?.[0]} alt={item.name} className="w-20 h-20 object-cover rounded-lg" />
                      <div className="flex-1">
                        <h3 className="font-bold text-[#1A1A1A]">{item.name}</h3>
                        {item.food_choices && (
                          <p className="text-xs text-[#5C5C5C] mt-1">
                            {item.food_choices.protein} • {item.food_choices.sauce}
                            {item.food_choices.drink !== "none" && ` • ${item.food_choices.drink}`}
                          </p>
                        )}
                        <p className="text-[#E25822] font-bold mt-1">${item.itemPrice.toFixed(2)}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={() => updateCartQty(item.cartItemId, item.qty - 1)} className="w-8 h-8 bg-white rounded-lg border border-[#E5E0D8] flex items-center justify-center hover:border-[#E25822] transition-colors">
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center font-bold">{item.qty}</span>
                          <button onClick={() => updateCartQty(item.cartItemId, item.qty + 1)} className="w-8 h-8 bg-white rounded-lg border border-[#E5E0D8] flex items-center justify-center hover:border-[#E25822] transition-colors">
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <button onClick={() => removeFromCart(item.cartItemId)} className="text-gray-400 hover:text-red-500 transition-colors">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {cart.length > 0 && (
              <div className="p-6 border-t border-[#E5E0D8] bg-[#FAF8F5]">
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-[#5C5C5C]">
                    <span>Subtotal</span>
                    <span>${cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#5C5C5C]">
                    <span>Shipping</span>
                    <span className={shippingCost === 0 ? "text-green-600 font-medium" : ""}>{shippingCost === 0 ? "FREE" : `$${shippingCost.toFixed(2)}`}</span>
                  </div>
                  {cartTotal < 50 && (
                    <p className="text-xs text-[#E25822]">Add ${(50 - cartTotal).toFixed(2)} more for free shipping!</p>
                  )}
                  <div className="flex justify-between text-xl font-bold pt-2 border-t border-[#E5E0D8]">
                    <span>Total</span>
                    <span className="text-[#E25822]">${(cartTotal + shippingCost).toFixed(2)}</span>
                  </div>
                </div>
                <button 
                  data-testid="checkout-btn"
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  {checkoutLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Checkout Securely
                    </>
                  )}
                </button>
                <div className="flex items-center justify-center gap-4 mt-4 text-xs text-[#8C8C8C]">
                  <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> SSL Encrypted</span>
                  <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" /> Stripe</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[#E5E0D8]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setActiveTab("shop")}>
            <div className="w-10 h-10 bg-gradient-to-br from-[#E25822] to-[#B43A1B] rounded-xl flex items-center justify-center">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-black text-xl text-[#1A1A1A]" style={{ fontFamily: 'Outfit' }}>T's BBQ</h1>
              <p className="text-xs text-[#5C5C5C] -mt-1">Magic Box</p>
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {[
              { id: "shop", label: "Shop" },
              { id: "faq", label: "FAQ" },
              { id: "contact", label: "Contact" },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`font-medium transition-colors ${activeTab === tab.id ? "text-[#E25822]" : "text-[#5C5C5C] hover:text-[#1A1A1A]"}`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
          
          <button 
            data-testid="cart-toggle"
            onClick={() => setShowCart(true)} 
            className="relative p-3 bg-white rounded-xl border border-[#E5E0D8] hover:border-[#E25822] hover:shadow-lg transition-all"
          >
            <ShoppingCart className="w-5 h-5 text-[#2A2A2A]" />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 w-6 h-6 bg-[#E25822] text-white rounded-full text-xs flex items-center justify-center font-bold">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Main Content */}
      {activeTab === "shop" && (
        <>
          {/* Hero Section */}
          <section className="relative overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 py-12 lg:py-20">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="stagger-children">
                  <span className="inline-block px-4 py-2 bg-[#E25822]/10 text-[#E25822] rounded-full text-sm font-bold uppercase tracking-widest mb-6 animate-fade-up opacity-0">
                    All-In-One BBQ Experience
                  </span>
                  <h1 className="text-5xl lg:text-7xl font-black uppercase tracking-tighter mb-6 animate-fade-up opacity-0" style={{ fontFamily: 'Outfit' }}>
                    <span className="text-[#1A1A1A]">Small Box.</span>
                    <br />
                    <span className="text-[#E25822]">Big Smoke.</span>
                  </h1>
                  <p className="text-xl text-[#5C5C5C] mb-8 leading-relaxed animate-fade-up opacity-0">
                    Grill anytime, anywhere with our all-in-one portable BBQ meal kits. 
                    <span className="accent-font text-2xl text-[#E25822]"> No gas. No electricity. Just fire & flavor.</span>
                  </p>
                  <div className="flex flex-wrap gap-4 animate-fade-up opacity-0">
                    <button 
                      onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Flame className="w-5 h-5" />
                      Shop Now
                    </button>
                    <div className="flex items-center gap-2 px-6 py-4 bg-white rounded-xl border border-[#E5E0D8]">
                      <StarRating rating={5} />
                      <span className="text-sm font-medium text-[#5C5C5C]">500+ Happy Customers</span>
                    </div>
                  </div>
                </div>
                
                <div className="relative animate-fade-up opacity-0" style={{ animationDelay: '0.4s' }}>
                  <div className="absolute inset-0 bg-gradient-to-br from-[#E25822]/20 to-transparent rounded-3xl blur-3xl"></div>
                  <img 
                    src={`${BACKEND_URL}/api/promo-images/bento_grill_kit_main`}
                    alt="EmberBentoBox BBQ Kit" 
                    className="relative rounded-3xl shadow-2xl animate-float"
                  />
                  <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-[#E5E0D8]">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-bold text-[#1A1A1A]">Ready in 3-5 min</p>
                        <p className="text-sm text-[#5C5C5C]">Self-heating design</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Trust Badges */}
          <section className="py-8 bg-white border-y border-[#E5E0D8]">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { icon: Truck, text: "Free Shipping $50+" },
                  { icon: RefreshCw, text: "30-Day Returns" },
                  { icon: Shield, text: "Secure Checkout" },
                  { icon: Award, text: "Quality Guaranteed" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-center gap-3 py-2">
                    <item.icon className="w-6 h-6 text-[#E25822]" />
                    <span className="font-medium text-[#2A2A2A]">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Products Section */}
          <section id="products" className="py-16 lg:py-24">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="text-4xl lg:text-5xl font-extrabold uppercase tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
                  Choose Your <span className="text-[#E25822]">BBQ Experience</span>
                </h2>
                <p className="text-[#5C5C5C] text-lg max-w-2xl mx-auto">
                  From complete meal kits with food to DIY grill boxes - we've got you covered
                </p>
              </div>

              {/* Product Cards */}
              <div className="grid md:grid-cols-2 gap-8 mb-16">
                {products.map((product) => (
                  <div 
                    key={product.id}
                    data-testid={`product-card-${product.id}`}
                    onClick={() => { setSelectedProduct(product); setSelectedImage(0); }}
                    className={`relative bg-white rounded-3xl overflow-hidden cursor-pointer hover-lift border-2 transition-all ${
                      selectedProduct?.id === product.id 
                        ? 'border-[#E25822] shadow-xl' 
                        : 'border-[#E5E0D8] hover:border-[#E25822]/50'
                    }`}
                  >
                    <div className={`absolute top-4 left-4 z-10 px-4 py-2 rounded-full text-white text-sm font-bold uppercase tracking-wide ${
                      product.badge === "BEST SELLER" ? "bg-[#E25822]" : "bg-green-600"
                    }`}>
                      {product.badge}
                    </div>
                    
                    <div className="aspect-[4/3] overflow-hidden img-zoom">
                      <img 
                        src={PRODUCT_IMAGES[product.id]?.[0]} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: 'Outfit' }}>{product.name}</h3>
                      <p className="text-[#5C5C5C] mb-4">{product.tagline}</p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-baseline gap-3">
                          <span className="text-3xl font-black text-[#E25822]">${product.price.toFixed(2)}</span>
                          <span className="text-lg text-[#8C8C8C] line-through">${product.original_price.toFixed(2)}</span>
                        </div>
                        <span className="px-3 py-1 bg-red-100 text-red-600 rounded-full text-sm font-bold">
                          -{product.discount}%
                        </span>
                      </div>
                    </div>
                    
                    {selectedProduct?.id === product.id && (
                      <div className="absolute top-4 right-4 w-8 h-8 bg-[#E25822] rounded-full flex items-center justify-center">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Selected Product Details */}
              {selectedProduct && (
                <div className="bg-white rounded-3xl p-8 lg:p-12 border border-[#E5E0D8] shadow-lg">
                  <div className="grid lg:grid-cols-2 gap-12">
                    {/* Images */}
                    <div className="space-y-4">
                      <div className="aspect-square rounded-2xl overflow-hidden border border-[#E5E0D8] img-zoom">
                        <img 
                          src={PRODUCT_IMAGES[selectedProduct.id]?.[selectedImage]} 
                          alt={selectedProduct.name}
                          className="w-full h-full object-contain bg-[#FAF8F5]"
                        />
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        {PRODUCT_IMAGES[selectedProduct.id]?.map((img, i) => (
                          <button
                            key={i}
                            onClick={() => setSelectedImage(i)}
                            className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                              selectedImage === i ? "border-[#E25822] ring-4 ring-[#E25822]/20" : "border-[#E5E0D8] hover:border-[#E25822]/50"
                            }`}
                          >
                            <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Details */}
                    <div className="space-y-6">
                      <div>
                        <span className={`inline-block px-4 py-2 rounded-full text-white text-sm font-bold uppercase tracking-wide mb-4 ${
                          selectedProduct.badge === "BEST SELLER" ? "bg-[#E25822]" : "bg-green-600"
                        }`}>
                          {selectedProduct.badge}
                        </span>
                        <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Outfit' }}>
                          {selectedProduct.name}
                        </h2>
                        <p className="text-lg text-[#5C5C5C] mt-2">{selectedProduct.tagline}</p>
                      </div>

                      <div className="flex items-center gap-3">
                        <StarRating rating={productReviews?.average_rating || 5} size="lg" />
                        <span className="text-[#5C5C5C] font-medium">({productReviews?.total || 0} reviews)</span>
                      </div>

                      <div className="flex items-baseline gap-4">
                        <span className="text-4xl font-black text-[#1A1A1A]">${selectedProduct.price.toFixed(2)}</span>
                        <span className="text-xl text-[#8C8C8C] line-through">${selectedProduct.original_price.toFixed(2)}</span>
                        <span className="px-4 py-2 bg-red-100 text-red-600 rounded-full font-bold">
                          Save {selectedProduct.discount}%
                        </span>
                      </div>

                      <p className="text-[#5C5C5C] leading-relaxed">{selectedProduct.description}</p>

                      {/* Food Customizer */}
                      {selectedProduct.has_food_options && foodOptions && (
                        <div className="bg-gradient-to-br from-[#FAF8F5] to-[#E25822]/5 rounded-2xl p-6 border border-[#E5E0D8]">
                          <h3 className="text-xl font-bold text-[#1A1A1A] mb-6 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                            <ChefHat className="w-6 h-6 text-[#E25822]" />
                            Customize Your Meal
                          </h3>
                          
                          <div className="mb-6">
                            <label className="flex items-center gap-2 text-sm font-bold text-[#2A2A2A] uppercase tracking-wide mb-3">
                              <UtensilsCrossed className="w-4 h-4 text-[#E25822]" />
                              Choose Protein
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                              {foodOptions.proteins.map(p => (
                                <button
                                  key={p.id}
                                  onClick={() => setFoodChoices({...foodChoices, protein: p.id})}
                                  className={`selection-card p-4 text-center ${foodChoices.protein === p.id ? 'active' : ''}`}
                                >
                                  <span className="font-bold text-lg">{p.name}</span>
                                  {p.price > 0 && <span className="block text-sm text-[#E25822] font-semibold">+${p.price.toFixed(2)}</span>}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="mb-6">
                            <label className="flex items-center gap-2 text-sm font-bold text-[#2A2A2A] uppercase tracking-wide mb-3">
                              <Package className="w-4 h-4 text-green-600" />
                              Included Sides
                            </label>
                            <div className="flex gap-3">
                              {foodOptions.sides.map(s => (
                                <span key={s.id} className="px-4 py-2 bg-green-100 text-green-700 rounded-xl text-sm font-bold flex items-center gap-2">
                                  <Check className="w-4 h-4" /> {s.name}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="mb-6">
                            <label className="flex items-center gap-2 text-sm font-bold text-[#2A2A2A] uppercase tracking-wide mb-3">
                              <Droplets className="w-4 h-4 text-[#E25822]" />
                              Choose Sauce
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                              {foodOptions.sauces.map(s => (
                                <button
                                  key={s.id}
                                  onClick={() => setFoodChoices({...foodChoices, sauce: s.id})}
                                  className={`selection-card p-4 text-center ${foodChoices.sauce === s.id ? 'active' : ''}`}
                                >
                                  <span className="font-bold">{s.name}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <label className="flex items-center gap-2 text-sm font-bold text-[#2A2A2A] uppercase tracking-wide mb-3">
                              <Wine className="w-4 h-4 text-[#E25822]" />
                              Add a Drink?
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                              {foodOptions.drinks.map(d => (
                                <button
                                  key={d.id}
                                  onClick={() => setFoodChoices({...foodChoices, drink: d.id})}
                                  className={`selection-card p-4 text-center ${foodChoices.drink === d.id ? 'active' : ''}`}
                                >
                                  <span className="font-bold">{d.name}</span>
                                  {d.price > 0 && <span className="block text-sm text-[#E25822]">+${d.price.toFixed(2)}</span>}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="bg-[#FAF8F5] rounded-2xl p-6">
                        <h3 className="font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                          <Package className="w-5 h-5 text-[#E25822]" />
                          What's Included
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                          {selectedProduct.includes.map((item, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <Check className="w-5 h-5 text-green-600 shrink-0" />
                              <span className="text-[#5C5C5C]">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <button
                        data-testid="add-to-cart"
                        onClick={() => addToCart(selectedProduct, 1, selectedProduct.has_food_options ? foodChoices : null)}
                        disabled={!selectedProduct.in_stock}
                        className="btn-primary w-full flex items-center justify-center gap-3 text-lg py-5"
                      >
                        <ShoppingCart className="w-6 h-6" />
                        Add to Cart — ${calculateItemPrice(selectedProduct, selectedProduct.has_food_options ? foodChoices : null).toFixed(2)}
                      </button>

                      <div className="flex items-center justify-center gap-6 text-sm text-[#8C8C8C]">
                        <span className="flex items-center gap-2"><Shield className="w-4 h-4" /> Secure</span>
                        <span className="flex items-center gap-2"><Truck className="w-4 h-4" /> Fast Shipping</span>
                        <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4" /> 30-Day Returns</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Bundle Deal */}
          <section className="py-16 lg:py-24 bg-[#2A2A2A]">
            <div className="max-w-7xl mx-auto px-6">
              <div className="bg-gradient-to-br from-[#E25822]/20 to-transparent rounded-3xl p-8 lg:p-12 border border-[#E25822]/30">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <span className="inline-block px-4 py-2 bg-[#E25822] text-white rounded-full text-sm font-bold uppercase tracking-wide mb-4">
                      <Sparkles className="w-4 h-4 inline mr-2" />
                      Bundle & Save
                    </span>
                    <h2 className="text-4xl lg:text-5xl font-black text-white uppercase tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
                      Get Both & Save $12!
                    </h2>
                    <p className="text-white/80 text-lg mb-6">
                      EmberBentoBox + Disposable Grill Box — Everything you need for the ultimate BBQ experience
                    </p>
                    <ul className="space-y-2 text-white/70">
                      <li className="flex items-center gap-2"><Check className="w-5 h-5 text-green-400" /> Complete meal kit with food</li>
                      <li className="flex items-center gap-2"><Check className="w-5 h-5 text-green-400" /> Extra grill for friends</li>
                      <li className="flex items-center gap-2"><Check className="w-5 h-5 text-green-400" /> Free shipping included</li>
                    </ul>
                  </div>
                  <div className="text-center lg:text-right">
                    <p className="text-white/50 line-through text-2xl">$54.99</p>
                    <p className="text-5xl font-black text-[#E25822]">$42.99</p>
                    <button
                      data-testid="add-bundle-btn"
                      onClick={() => {
                        addToCart({
                          id: "bundle",
                          name: "Bundle: EmberBentoBox + Grill Box",
                          price: 42.99,
                          has_food_options: false,
                        }, 1, null);
                      }}
                      className="mt-6 btn-primary text-lg px-10 py-5"
                    >
                      <Flame className="w-5 h-5 inline mr-2" />
                      Add Bundle to Cart
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Reviews */}
          <section className="py-16 lg:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
                <div>
                  <h2 className="text-4xl font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Outfit' }}>
                    Customer <span className="text-[#E25822]">Reviews</span>
                  </h2>
                  <p className="text-[#5C5C5C] mt-2">See what our customers are saying</p>
                </div>
                <button
                  onClick={() => setShowReviewForm(!showReviewForm)}
                  className="px-6 py-3 bg-[#FAF8F5] border border-[#E5E0D8] rounded-xl font-bold hover:border-[#E25822] transition-colors flex items-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  Write a Review
                </button>
              </div>

              {showReviewForm && (
                <div className="bg-[#FAF8F5] rounded-2xl p-8 mb-8 border border-[#E5E0D8]">
                  <h3 className="text-xl font-bold mb-6">Share Your Experience</h3>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Your name"
                      value={newReview.name}
                      onChange={(e) => setNewReview({...newReview, name: e.target.value})}
                      className="w-full p-4 border border-[#E5E0D8] rounded-xl focus:border-[#E25822] focus:ring-2 focus:ring-[#E25822]/20 outline-none"
                    />
                    <div>
                      <p className="text-sm font-medium text-[#5C5C5C] mb-2">Rating</p>
                      <StarRating rating={newReview.rating} size="lg" interactive onChange={(r) => setNewReview({...newReview, rating: r})} />
                    </div>
                    <textarea
                      placeholder="Tell us about your BBQ experience..."
                      value={newReview.comment}
                      onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                      className="w-full p-4 border border-[#E5E0D8] rounded-xl h-32 focus:border-[#E25822] focus:ring-2 focus:ring-[#E25822]/20 outline-none resize-none"
                    />
                    <button onClick={submitReview} className="btn-primary">
                      Submit Review
                    </button>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {productReviews?.reviews?.length > 0 ? (
                  productReviews.reviews.map(review => (
                    <ReviewCard key={review.id} review={review} />
                  ))
                ) : (
                  <div className="col-span-full text-center py-16">
                    <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-[#5C5C5C]">No reviews yet. Be the first to review!</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-16 lg:py-24 bg-[#FAF8F5]">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: Flame, title: "No Gas/Electricity", desc: "Just fire & charcoal", color: "text-[#E25822]" },
                  { icon: Clock, title: "Ready in 3-5 Min", desc: "Fast-igniting design", color: "text-blue-600" },
                  { icon: Package, title: "All-in-One Kit", desc: "Everything included", color: "text-green-600" },
                  { icon: Leaf, title: "Eco-Friendly", desc: "Disposable & clean", color: "text-emerald-600" },
                ].map((f, i) => (
                  <div key={i} className="bg-white rounded-2xl p-8 text-center border border-[#E5E0D8] hover-lift">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#FAF8F5] flex items-center justify-center ${f.color}`}>
                      <f.icon className="w-8 h-8" />
                    </div>
                    <h3 className="font-bold text-[#1A1A1A] text-lg mb-1" style={{ fontFamily: 'Outfit' }}>{f.title}</h3>
                    <p className="text-[#5C5C5C]">{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* FAQ Page */}
      {activeTab === "faq" && (
        <section className="py-16 lg:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <HelpCircle className="w-16 h-16 text-[#E25822] mx-auto mb-4" />
              <h1 className="text-4xl lg:text-5xl font-extrabold uppercase tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
                Frequently Asked <span className="text-[#E25822]">Questions</span>
              </h1>
              <p className="text-[#5C5C5C] text-lg">Everything you need to know about our BBQ kits</p>
            </div>
            
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <FAQItem
                  key={i}
                  faq={faq}
                  isOpen={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                />
              ))}
            </div>
            
            <div className="mt-12 text-center p-8 bg-white rounded-2xl border border-[#E5E0D8]">
              <p className="text-[#5C5C5C] mb-4">Still have questions?</p>
              <button
                onClick={() => setActiveTab("contact")}
                className="btn-primary"
              >
                <Mail className="w-5 h-5 inline mr-2" />
                Contact Us
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Contact Page */}
      {activeTab === "contact" && storeInfo && (
        <section className="py-16 lg:py-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <MessageCircle className="w-16 h-16 text-[#E25822] mx-auto mb-4" />
              <h1 className="text-4xl lg:text-5xl font-extrabold uppercase tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
                Get In <span className="text-[#E25822]">Touch</span>
              </h1>
              <p className="text-[#5C5C5C] text-lg">We'd love to hear from you</p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div className="space-y-8">
                <div className="bg-white rounded-2xl p-8 border border-[#E5E0D8]">
                  <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit' }}>Contact Information</h2>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#E25822]/10 rounded-xl flex items-center justify-center">
                        <Mail className="w-6 h-6 text-[#E25822]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#5C5C5C]">Email</p>
                        <p className="font-bold">{storeInfo.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#E25822]/10 rounded-xl flex items-center justify-center">
                        <Phone className="w-6 h-6 text-[#E25822]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#5C5C5C]">Phone</p>
                        <p className="font-bold">{storeInfo.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#E25822]/10 rounded-xl flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-[#E25822]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#5C5C5C]">Address</p>
                        <p className="font-bold">{storeInfo.address.street}</p>
                        <p className="text-[#5C5C5C]">{storeInfo.address.city}, {storeInfo.address.state} {storeInfo.address.zip}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#E25822]/10 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-[#E25822]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#5C5C5C]">Hours</p>
                        <p className="font-bold">{storeInfo.hours}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl p-8 border border-[#E5E0D8]">
                  <h3 className="font-bold mb-4">Follow Us</h3>
                  <div className="flex gap-4">
                    <a href={`https://instagram.com/${storeInfo.social.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#FAF8F5] rounded-xl flex items-center justify-center hover:bg-[#E25822] hover:text-white transition-colors">
                      <Instagram className="w-6 h-6" />
                    </a>
                    <a href={`https://facebook.com/${storeInfo.social.facebook}`} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#FAF8F5] rounded-xl flex items-center justify-center hover:bg-[#E25822] hover:text-white transition-colors">
                      <Facebook className="w-6 h-6" />
                    </a>
                    <a href={`https://twitter.com/${storeInfo.social.twitter.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#FAF8F5] rounded-xl flex items-center justify-center hover:bg-[#E25822] hover:text-white transition-colors">
                      <Twitter className="w-6 h-6" />
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8 border border-[#E5E0D8]">
                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit' }}>Send a Message</h2>
                {contactStatus && (
                  <div className={`p-4 rounded-xl mb-6 ${contactStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {contactStatus.message}
                  </div>
                )}
                <form onSubmit={handleContact} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    required
                    className="w-full p-4 border border-[#E5E0D8] rounded-xl focus:border-[#E25822] focus:ring-2 focus:ring-[#E25822]/20 outline-none"
                  />
                  <input
                    type="email"
                    placeholder="Your email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    required
                    className="w-full p-4 border border-[#E5E0D8] rounded-xl focus:border-[#E25822] focus:ring-2 focus:ring-[#E25822]/20 outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Subject"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                    className="w-full p-4 border border-[#E5E0D8] rounded-xl focus:border-[#E25822] focus:ring-2 focus:ring-[#E25822]/20 outline-none"
                  />
                  <textarea
                    placeholder="Your message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    required
                    rows={5}
                    className="w-full p-4 border border-[#E5E0D8] rounded-xl focus:border-[#E25822] focus:ring-2 focus:ring-[#E25822]/20 outline-none resize-none"
                  />
                  <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2">
                    <Send className="w-5 h-5" />
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Newsletter Section */}
      <section className="py-16 bg-[#E25822]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
            Get 10% Off Your First Order
          </h2>
          <p className="text-white/80 mb-8">Subscribe to our newsletter for exclusive deals, recipes, and grilling tips!</p>
          
          {newsletterStatus && (
            <div className={`p-4 rounded-xl mb-6 max-w-md mx-auto ${newsletterStatus.success ? 'bg-green-500/20 text-white' : 'bg-red-500/20 text-white'}`}>
              {newsletterStatus.message}
            </div>
          )}
          
          <form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              required
              className="flex-1 px-6 py-4 rounded-xl border-2 border-white/30 bg-white/10 text-white placeholder-white/60 focus:border-white focus:bg-white/20 outline-none"
            />
            <button type="submit" className="px-8 py-4 bg-white text-[#E25822] font-bold rounded-xl hover:bg-[#FAF8F5] transition-colors flex items-center justify-center gap-2">
              Subscribe
              <ArrowRight className="w-5 h-5" />
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2A2A2A] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#E25822] to-[#B43A1B] rounded-xl flex items-center justify-center">
                  <Flame className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="font-black text-xl" style={{ fontFamily: 'Outfit' }}>T's BBQ Magic Box</h2>
                  <p className="text-white/60 text-sm">Small Box. Big Smoke.</p>
                </div>
              </div>
              <p className="text-white/60 text-sm">Grill anytime, anywhere with our portable BBQ kits. No gas, no electricity - just fire and flavor.</p>
            </div>
            
            {/* Quick Links */}
            <div>
              <h3 className="font-bold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-white/60">
                <li><button onClick={() => setActiveTab("shop")} className="hover:text-white transition-colors">Shop</button></li>
                <li><button onClick={() => setActiveTab("faq")} className="hover:text-white transition-colors">FAQ</button></li>
                <li><button onClick={() => setActiveTab("contact")} className="hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>
            
            {/* Customer Service */}
            <div>
              <h3 className="font-bold mb-4">Customer Service</h3>
              <ul className="space-y-2 text-white/60">
                <li><a href="#" className="hover:text-white transition-colors">Shipping Info</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Return Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Track Order</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
            
            {/* Contact */}
            {storeInfo && (
              <div>
                <h3 className="font-bold mb-4">Contact Us</h3>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> {storeInfo.email}</li>
                  <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> {storeInfo.phone}</li>
                  <li className="flex items-center gap-2"><Clock className="w-4 h-4" /> {storeInfo.hours}</li>
                </ul>
                <div className="flex gap-3 mt-4">
                  <a href="#" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#E25822] transition-colors">
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a href="#" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#E25822] transition-colors">
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a href="#" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#E25822] transition-colors">
                    <Twitter className="w-5 h-5" />
                  </a>
                </div>
              </div>
            )}
          </div>
          
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-sm">© 2026 T's BBQ Magic Box. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/visa/visa-original.svg" alt="Visa" className="h-8 opacity-60" />
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mastercard/mastercard-original.svg" alt="Mastercard" className="h-8 opacity-60" />
              <span className="text-white/40 text-sm flex items-center gap-1"><Shield className="w-4 h-4" /> Secured by Stripe</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
